import { useNotifications } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Chip, Paper, TextField, Typography } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { createFileRoute } from '@tanstack/react-router'
import { Copy } from 'lucide-react'
import { useMemo, useState } from 'react'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import PlusIcon from '../../../components/icons/PlusIcon'
import Page from '../../../components/Page'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/notifications/')({
  staticData: {
    permission: ['read', 'notifications.notifications.notification'],
  },
  component: NotificationsPage,
})
withRoutePermissions(Route)

const SMALL_TAB = 120
const TAB_WIDTH = 160
const BIGGER_TAB = 260

function prettifyActionPath(value?: string): string {
  if (value?.startsWith('anti-gaspi/')) {
    return 'Redirige vers un produit'
  }
  if (value === 'anti-gaspi') {
    return 'Redirige vers la page anti-gaspi'
  }
  if (value === 'product') {
    return 'Redirige vers un page produit'
  }
  return 'Aucune action'
}

const notificationsDefs: GridColDef[] = [
  {
    field: 'title',
    headerName: 'Titre',
    width: TAB_WIDTH,
  },
  {
    field: 'subTitle',
    headerName: 'Sous-titre',
    width: TAB_WIDTH,
  },
  {
    field: 'text',
    headerName: 'Contenu',
    width: BIGGER_TAB,
  },
  {
    field: 'onlyUsers',
    headerName: 'Inscrits seulement',
    type: 'boolean',
    width: BIGGER_TAB,
  },
  {
    field: 'data',
    headerName: 'Action',
    renderCell: (field) => <Chip label={prettifyActionPath(field.value?.path)} />,
    width: BIGGER_TAB,
  },
  {
    field: 'recipients',
    headerName: 'Destinataires',
    width: SMALL_TAB,
  },
  {
    field: 'interactionsCount',
    headerName: 'Interactions',
    width: SMALL_TAB,
  },
  {
    field: 'createdAt',
    headerName: 'Créée le',
    valueFormatter: (params) => new Date(params.value as string).toLocaleString(),
    width: BIGGER_TAB,
  },
]

function NotificationsPage() {
  const { data: notifications, isLoading: notificationsLoading } = useNotifications()
  const [notificationSearch, setNotificationSearch] = useState('')
  const [notificationSelection, setNotificationSelection] = useState<number[]>([])

  const notificationRows = useMemo(() => {
    if (!notifications) {
      return []
    }
    return notifications
      .filter((notification) => includesLowerCase(notification.title, notificationSearch))
      .map((notification) => ({
        ...notification,
      }))
  }, [notifications, notificationSearch])

  return (
    <Page>
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Notifications
        </Typography>
        <AuthorizedButtonLink
          variant="contained"
          color="secondary"
          sx={{ marginRight: '16px' }}
          to="/notifications/$id"
          legacyRights={['ADMIN']}
          params={{ id: notificationSelection[0] }}
          disabled={notificationSelection.length !== 1}
        >
          Voir
        </AuthorizedButtonLink>
        <AuthorizedButtonLink
          variant="contained"
          color="secondary"
          sx={{ marginRight: '16px' }}
          to="/notifications/new"
          legacyRights={['ADMIN']}
          search={{ notificationId: notificationSelection[0] }}
          disabled={notificationSelection.length !== 1}
        >
          <Copy />
          Dupliquer
        </AuthorizedButtonLink>
        <AuthorizedButtonLink
          variant="contained"
          sx={{ marginRight: '16px' }}
          to="/notifications/new"
          legacyRights={['ADMIN']}
        >
          <PlusIcon />
          Créer une notification
        </AuthorizedButtonLink>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={notificationSearch}
            onChange={(e) => setNotificationSearch(e.target.value)}
          />
        </div>
        <div style={{ flexGrow: 1 }} />
        <SimpleDataGrid
          columns={notificationsDefs}
          rows={notificationRows}
          loading={notificationsLoading}
          onRowSelectionModelChange={(newSelection) =>
            setNotificationSelection(newSelection as number[])
          }
        />
      </Paper>
    </Page>
  )
}
